import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function BookingRulesTab() {
  const { currentOrgId } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (currentOrgId) load(); }, [currentOrgId]);

  const load = async () => {
    try { const r = await apiFetch(`/scheduling/booking-rules?organization_id=${currentOrgId}`); setForm(r); }
    catch (e) { toast.error(e.message); }
  };

  const save = async () => {
    setSaving(true);
    try { await apiFetch("/scheduling/booking-rules", {
      method: "PUT", body: JSON.stringify({ ...form, organization_id: currentOrgId })
    }); toast.success("Rules saved"); load(); } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (!form) return <p className="text-sm text-[#A8A29E]">Loading…</p>;

  const NumField = ({ label, k, min = 0, unit, hint }) => (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2 mt-1.5">
        <Input type="number" min={min} value={form[k] ?? ""}
               onChange={(e) => setForm({...form, [k]: e.target.value === "" ? null : Number(e.target.value)})}
               className="w-32" data-testid={`rules-${k}`} />
        {unit && <span className="text-xs text-[#57534E]">{unit}</span>}
      </div>
      {hint && <p className="text-xs text-[#A8A29E] mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-[#57534E]">Global defaults. Individual appointment types can override any setting.</p>

      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-5 space-y-5">
        <h2 className="font-display font-semibold text-base">Booking notice &amp; window</h2>
        <div className="grid grid-cols-2 gap-4">
          <NumField label="Minimum booking notice" k="min_notice_minutes" unit="minutes" hint="4h = 240" />
          <NumField label="Maximum booking window" k="max_advance_days" unit="days" hint="e.g. 60" />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-5 space-y-5">
        <h2 className="font-display font-semibold text-base">Time slots &amp; buffers</h2>
        <div className="grid grid-cols-3 gap-4">
          <NumField label="Slot increment" k="increment_minutes" min={5} unit="minutes" />
          <NumField label="Buffer before" k="buffer_before" unit="minutes" />
          <NumField label="Buffer after" k="buffer_after" unit="minutes" />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-5 space-y-5">
        <h2 className="font-display font-semibold text-base">Limits</h2>
        <div className="grid grid-cols-2 gap-4">
          <NumField label="Max appointments per day" k="daily_limit" unit="per day" hint="Blank = unlimited" />
          <NumField label="Max appointments per week" k="weekly_limit" unit="per week" hint="Blank = unlimited" />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-5 space-y-5">
        <h2 className="font-display font-semibold text-base">Cancellation &amp; rescheduling</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.allow_client_cancel}
                onCheckedChange={(v) => setForm({...form, allow_client_cancel: v})}
                data-testid="rules-allow-cancel" />
              Clients can cancel
            </label>
            <div className="mt-2"><NumField label="Cancel deadline" k="cancel_deadline_hours" unit="hours before" /></div>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.allow_client_reschedule}
                onCheckedChange={(v) => setForm({...form, allow_client_reschedule: v})}
                data-testid="rules-allow-reschedule" />
              Clients can reschedule
            </label>
            <div className="mt-2"><NumField label="Reschedule deadline" k="reschedule_deadline_hours" unit="hours before" /></div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={!!form.allow_same_day}
            onCheckedChange={(v) => setForm({...form, allow_same_day: v})}
            data-testid="rules-allow-sameday" />
          Allow same-day bookings
        </label>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="rules-save"
                className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
          <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save Booking Rules"}
        </Button>
      </div>
    </div>
  );
}
